import { AvailableMoves, Dice } from '@kfiroo/backgammon-core'
import { isNumber, last, noop, uniq } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useToast } from '../components/Toast'
import { AnimatedMove, BoardInfo, PlayerType } from '../GameApi/BaseGameApi'
import { PlayingGameApi, ReadOnlyGameApi } from '../GameApi/InProgressGameApi'

export interface GameLogic {
  dropTargets?: number[]
  hitTargets?: number[]
  selected: number | null
  onLaneClicked: (lane: number) => void | Promise<void>

  board: BoardInfo
  liveMoves: AnimatedMove[]
  turn: PlayerType | null
  /** Authoritative turn pointer (0/1); changes every turn so the animated board
   *  can reset on a turn boundary instead of mistaking a cleared turn for an undo. */
  turnKey: number
  dice: Dice | null
  endTurnEnabled: boolean
  onEndTurn: () => void | Promise<void>
  undoEnabled: boolean
  onUndo: () => void | Promise<void>
  onRoll: () => void | Promise<void>
}

export function usePlayingGameLogic(gameApi: PlayingGameApi): GameLogic {
  const [board, setBoard] = useState<BoardInfo>(gameApi.getBoard())
  const [selected, setSelected] = useState<number | null>(null)
  const [endTurnEnabled, setEndTurnEnabled] = useState(false)
  const [undoEnabled, setUndoEnabled] = useState(false)
  const [, setSaving] = useState(false)
  const savingRef = useRef(false)
  const { showError } = useToast()

  const availableMoves = gameApi.getValidMoves()
  const dropTargets = calcDropTargets(selected, availableMoves)
  const hitTargets = dropTargets.filter((lane) => gameApi.isOpenOpponent(lane))

  useEffect(() => {
    setBoard(gameApi.getBoard())
    setSelected(null)
    setEndTurnEnabled(gameApi.canEndTurn())
    setUndoEnabled(gameApi.canUndo())
  }, [gameApi])

  const onLaneClicked = useCallback(
    async (lane: number) => {
      if (selected === null) {
        if (availableMoves[lane]?.length) {
          setSelected(lane)
        }
        return
      }
      if (selected === lane) {
        setSelected(null)
        return
      }
      if (dropTargets.includes(lane)) {
        if (savingRef.current) return
        savingRef.current = true
        setSaving(true)
        setSelected(null)
        try {
          await gameApi.move(selected, lane)
        } catch {
          showError('Move not saved — try again')
          setSelected(selected) // move rolled back — restore selection to retry
        } finally {
          savingRef.current = false
          setSaving(false)
          setUndoEnabled(gameApi.canUndo())
          setEndTurnEnabled(gameApi.canEndTurn())
          setBoard(gameApi.getBoard())
        }
      }
    },
    [selected, availableMoves, gameApi, dropTargets, showError],
  )

  const onEndTurn = useCallback(async () => {
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    try {
      await gameApi.endTurn()
    } catch {
      showError('Turn not saved — try again')
    } finally {
      savingRef.current = false
      setSaving(false)
      setUndoEnabled(gameApi.canUndo())
      setEndTurnEnabled(gameApi.canEndTurn())
      setBoard(gameApi.getBoard())
    }
  }, [gameApi, showError])

  const onUndo = useCallback(async () => {
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    try {
      await gameApi.undo()
    } catch {
      showError('Undo not saved — try again')
    } finally {
      savingRef.current = false
      setSaving(false)
      setUndoEnabled(gameApi.canUndo())
      setEndTurnEnabled(gameApi.canEndTurn())
      setBoard(gameApi.getBoard())
    }
  }, [gameApi, showError])

  const onRoll = useCallback(async () => {
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    try {
      await gameApi.rollDice()
    } catch {
      showError('Roll not saved — try again')
    } finally {
      savingRef.current = false
      setSaving(false)
      setBoard(gameApi.getBoard())
    }
  }, [gameApi, showError])

  return {
    board,
    liveMoves: gameApi.getLiveMoves(),
    turn: gameApi.getTurn(),
    turnKey: gameApi.getTurnIndex(),
    dice: gameApi.getDice(),
    hitTargets,
    dropTargets,
    selected,
    onLaneClicked,
    endTurnEnabled,
    onEndTurn,
    undoEnabled,
    onUndo,
    onRoll,
  }
}

export function useReadOnlyGameLogic(gameApi: ReadOnlyGameApi): GameLogic {
  return {
    board: gameApi.getBoard(),
    liveMoves: gameApi.getLiveMoves(),
    turn: gameApi.getTurn(),
    turnKey: gameApi.getTurnIndex(),
    dice: gameApi.getDice(),
    hitTargets: [],
    dropTargets: [],
    selected: null,
    onLaneClicked: noop,
    endTurnEnabled: false,
    onEndTurn: noop,
    undoEnabled: false,
    onUndo: noop,
    onRoll: noop,
  }
}

function calcDropTargets(
  from: number | null,
  availableMoves: AvailableMoves,
): number[] {
  if (from === null || !availableMoves[from]) {
    return []
  }

  return uniq(
    availableMoves[from].map((moves) => last(moves)?.to).filter(isNumber),
  )
}
